/**
 * Search Filters Component
 *
 * Compact, visually appealing sidebar filters for searching listings
 */

import { useSearchStore } from "@/lib/store";
import { useState } from "react";

const boatTypes = [
  { id: "powerboat", label: "Power Boats", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
  { id: "sailboat", label: "Sailboats", icon: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" },
  { id: "yacht", label: "Yachts", icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" },
  { id: "catamaran", label: "Catamarans", icon: "M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" },
  { id: "fishing", label: "Fishing Boats", icon: "M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" },
  { id: "speedboat", label: "Speed Boats", icon: "M13 10V3L4 14h7v7l9-11h-7z" },
];

const priceRanges = [
  { label: "Under $50K", min: 0, max: 5000000 },
  { label: "$50K - $100K", min: 5000000, max: 10000000 },
  { label: "$100K - $250K", min: 10000000, max: 25000000 },
  { label: "$250K - $500K", min: 25000000, max: 50000000 },
  { label: "$500K+", min: 50000000, max: null },
];

export function SearchFilters() {
  const {
    filters,
    setSearch,
    setType,
    setPriceRange,
    setLocation,
    setLengthRange,
    resetFilters,
  } = useSearchStore();

  const [expandedSections, setExpandedSections] = useState({
    type: true,
    price: true,
    location: true,
    length: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const activeFilterCount = [
    filters.search,
    filters.location,
    filters.type.length > 0,
    filters.minPrice,
    filters.maxPrice,
    filters.minLength,
    filters.maxLength,
  ].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="card p-4">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search boats..."
            className="w-full pl-10 pr-4 py-3 border border-ash rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent text-sm bg-slate-50"
          />
          {filters.search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Boat Type */}
      <div className="card overflow-hidden">
        <button
          onClick={() => toggleSection("type")}
          className="flex justify-between items-center w-full p-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="font-semibold text-ink">Boat Type</span>
            {filters.type.length > 0 && (
              <span className="bg-accent/10 text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                {filters.type.length}
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.type ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSections.type && (
          <div className="px-4 pb-4 grid grid-cols-2 gap-2">
            {boatTypes.map((type) => {
              const isSelected = filters.type.includes(type.id);
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    if (isSelected) {
                      setType(filters.type.filter((t) => t !== type.id));
                    } else {
                      setType([...filters.type, type.id]);
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    isSelected
                      ? "bg-accent text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={type.icon} />
                  </svg>
                  <span className="truncate">{type.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Price Range */}
      <div className="card overflow-hidden">
        <button
          onClick={() => toggleSection("price")}
          className="flex justify-between items-center w-full p-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold text-ink">Price Range</span>
            {(filters.minPrice || filters.maxPrice) && (
              <span className="bg-accent/10 text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                Set
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.price ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSections.price && (
          <div className="px-4 pb-4 space-y-3">
            {/* Quick Price Buttons */}
            <div className="flex flex-wrap gap-2">
              {priceRanges.map((range) => {
                const isSelected = filters.minPrice === range.min && filters.maxPrice === range.max;
                return (
                  <button
                    key={range.label}
                    onClick={() => {
                      if (isSelected) {
                        setPriceRange(null, null);
                      } else {
                        setPriceRange(range.min, range.max);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      isSelected
                        ? "bg-accent text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {range.label}
                  </button>
                );
              })}
            </div>

            {/* Custom Range Inputs */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Min</label>
                <input
                  type="number"
                  value={filters.minPrice ? filters.minPrice / 100 : ""}
                  onChange={(e) =>
                    setPriceRange(
                      e.target.value ? parseInt(e.target.value) * 100 : null,
                      filters.maxPrice
                    )
                  }
                  placeholder="$0"
                  className="w-full px-3 py-2 border border-ash rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Max</label>
                <input
                  type="number"
                  value={filters.maxPrice ? filters.maxPrice / 100 : ""}
                  onChange={(e) =>
                    setPriceRange(
                      filters.minPrice,
                      e.target.value ? parseInt(e.target.value) * 100 : null
                    )
                  }
                  placeholder="No limit"
                  className="w-full px-3 py-2 border border-ash rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Location */}
      <div className="card overflow-hidden">
        <button
          onClick={() => toggleSection("location")}
          className="flex justify-between items-center w-full p-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-semibold text-ink">Location</span>
            {filters.location && (
              <span className="bg-accent/10 text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                1
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.location ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSections.location && (
          <div className="px-4 pb-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <input
                type="text"
                value={filters.location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, country, or region..."
                className="w-full pl-9 pr-4 py-2 border border-ash rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
              />
            </div>
          </div>
        )}
      </div>

      {/* Length */}
      <div className="card overflow-hidden">
        <button
          onClick={() => toggleSection("length")}
          className="flex justify-between items-center w-full p-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
            <span className="font-semibold text-ink">Length</span>
            {(filters.minLength || filters.maxLength) && (
              <span className="bg-accent/10 text-accent text-xs px-2 py-0.5 rounded-full font-medium">
                Set
              </span>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-slate-400 transition-transform ${expandedSections.length ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSections.length && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Min (ft)</label>
                <input
                  type="number"
                  value={filters.minLength || ""}
                  onChange={(e) =>
                    setLengthRange(
                      e.target.value ? parseInt(e.target.value) : null,
                      filters.maxLength
                    )
                  }
                  placeholder="0"
                  className="w-full px-3 py-2 border border-ash rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Max (ft)</label>
                <input
                  type="number"
                  value={filters.maxLength || ""}
                  onChange={(e) =>
                    setLengthRange(
                      filters.minLength,
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  placeholder="No limit"
                  className="w-full px-3 py-2 border border-ash rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reset Button */}
      {activeFilterCount > 0 && (
        <button
          onClick={resetFilters}
          className="w-full px-4 py-3 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition text-sm font-semibold flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Clear All Filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
}
